import { CalendarConnectionCard } from '@/features/account-calendar/calendar-connection-card'
import { LegalClarifications } from '@/features/account-calendar/legal-clarifications'
import { useCalendarConnections } from '@/features/account-calendar/use-calendar-connections'
import { ReconnectCalendarBanner } from '@/ui/core/reconnect-calendar-banner'
import { ConfirmationDialog, type ConfirmationDialogRef } from '@/ui/rapid-ui/structural'
import { type CalendarProvider, calendarProvider } from '@jobilla/nexus-sdk'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

/** OAuth error codes the callback controller redirects back with. */
type CalendarErrorCode =
	| 'cancelled'
	| 'invalid_state'
	| 'provider_mismatch'
	| 'exchange_failed'
	| 'invalid_provider'

type CalendarSearch = {
	calendar_connected?: string
	calendar_error?: string
}

/** Both calendar providers, in display order. One may be connected at a time. */
const PROVIDERS: CalendarProvider[] = [calendarProvider.google, calendarProvider.microsoft_365]

export const Route = createFileRoute('/_auth/account/calendar')({
	validateSearch: (search: Record<string, unknown>): CalendarSearch => ({
		calendar_connected: typeof search.calendar_connected === 'string' ? search.calendar_connected : undefined,
		calendar_error: typeof search.calendar_error === 'string' ? search.calendar_error : undefined,
	}),
	component: RouteComponent,
})

function RouteComponent() {
	const { t } = useTranslation('common')
	const search = Route.useSearch()
	const navigate = useNavigate()

	// Capture the callback outcome from the first render, then strip it from the
	// URL so a refresh or shared link doesn't re-show the banner.
	const outcome = useRef(search).current
	const justConnected = Boolean(outcome.calendar_connected)

	// Seed the callback error from the first render, but hold it as state so a
	// later connect/disconnect attempt can clear it — otherwise a stale callback
	// error would permanently mask a fresh connectFailed/disconnectFailed.
	const [callbackError, setCallbackError] = useState(outcome.calendar_error)

	useEffect(() => {
		if (search.calendar_connected || search.calendar_error) {
			void navigate({ to: '/account/calendar', search: {}, replace: true })
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const {
		connection,
		isLoading,
		isError,
		connect,
		connectingProvider,
		connectFailed,
		disconnect,
		disconnectFailed,
		isDisconnecting,
	} = useCalendarConnections()
	const disconnectDialog = useRef<ConfirmationDialogRef>(null)
	const isMicrosoft365Enabled = useBooleanFlagValue('microsoft-365-calendar', false)

	if (isLoading) {
		return (
			<div className="flex min-h-64 items-center justify-center text-sm text-grey-400">
				{t('account.calendar.loading')}
			</div>
		)
	}

	if (isError) {
		return (
			<div className="flex min-h-64 items-center justify-center text-sm text-red-500">
				{t('account.calendar.load_error')}
			</div>
		)
	}

	const handleConnect = (provider: CalendarProvider) => {
		setCallbackError(undefined)
		void connect(provider)
	}

	const handleDisconnect = async () => {
		if (!connection) {
			return
		}

		try {
			await disconnectDialog.current?.confirm({
				title: t('account.calendar.disconnect_dialog.title'),
				message: t('account.calendar.disconnect_dialog.message', { email: connection.accountEmail }),
				confirmText: t('account.calendar.disconnect_dialog.confirm'),
				cancelText: t('account.calendar.disconnect_dialog.cancel'),
			})
		} catch {
			// User cancelled the dialog — nothing to do. A real disconnect failure is
			// handled below by the hook (surfaced via disconnectFailed).
			return
		}

		setCallbackError(undefined)
		await disconnect(connection.id)
	}

	const statusError = callbackError
		? t(`account.calendar.status.error.${callbackError as CalendarErrorCode}`, {
			defaultValue: t('account.calendar.status.error.generic'),
		})
		: connectFailed
		? t('account.calendar.status.connect_failed')
		: disconnectFailed
		? t('account.calendar.status.disconnect_failed')
		: null

	return (
		<div className="mx-auto flex max-w-2xl flex-col gap-6 px-8 py-10">
			<div>
				<h1 className="text-2xl font-semibold text-grey-900">{t('account.calendar.title')}</h1>
				<p className="mt-1 text-sm text-grey-500">{t('account.calendar.description')}</p>
			</div>

			{justConnected
				? (
					<div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
						{t('account.calendar.status.connected')}
					</div>
				)
				: null}

			{statusError
				? (
					<div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
						{statusError}
					</div>
				)
				: null}

			<ReconnectCalendarBanner linkToSettings={false} />

			<LegalClarifications />

			<div className="flex flex-col gap-4">
				{PROVIDERS.map((provider) => {
					const state = connection?.provider === provider
						? 'connected'
						: provider === calendarProvider.microsoft_365 && !isMicrosoft365Enabled
						? 'unavailable'
						: connection
						? 'locked'
						: 'available'

					return (
						<CalendarConnectionCard
							key={provider}
							provider={provider}
							state={state}
							email={connection?.accountEmail}
							needsReconnect={connection?.needsReconnect}
							lockedByName={state === 'locked' && connection
								? t(`account.calendar.providers.${connection.provider}`)
								: undefined}
							connectingProvider={connectingProvider}
							isDisconnecting={isDisconnecting}
							onConnect={handleConnect}
							onDisconnect={() => void handleDisconnect()}
						/>
					)
				})}
			</div>

			<ConfirmationDialog ref={disconnectDialog} />
		</div>
	)
}
